<script lang="ts">
  /**
   * The transparent canvas over the video that shows everyone's pointers, pings
   * and ink, and, while `active`, turns our own mouse into them.
   *
   * Move to point, click to ping, drag to draw. Nothing here is encoded into the
   * stream: each screen paints from fractions of the frame (see `marks.ts`).
   */
  import { onDestroy, onMount } from 'svelte'
  import {
    MarkBoard,
    MarkSender,
    containRect,
    toFraction,
    type FrameRect,
    type MarkMessage,
    type MarkPoint,
  } from './marks'
  import type { ClientMessage } from './protocol'

  let {
    video,
    active,
    selfId,
    nameFor,
    send,
  }: {
    video: HTMLVideoElement | null
    active: boolean
    selfId: string | null
    nameFor: (peer: string) => string | undefined
    send: (message: Extract<ClientMessage, { type: 'mark' }>) => void
  } = $props()

  /** A drag shorter than this is a click, which pings. */
  const DRAG_PX = 6

  const board = new MarkBoard()
  const sender = new MarkSender((message) => send(message))
  let canvas = $state<HTMLCanvasElement | null>(null)
  let frame: number | null = null
  let size = { width: 0, height: 0, ratio: 1 }

  export function apply(message: MarkMessage) {
    board.apply(message, performance.now())
    wake()
  }

  export function drop(peer: string) {
    board.drop(peer)
    wake()
  }

  export function clear() {
    board.clear()
    wake()
  }

  function rect(): FrameRect | null {
    if (!video) return null
    return containRect(video.clientWidth, video.clientHeight, video.videoWidth, video.videoHeight)
  }

  function fit() {
    if (!canvas) return
    const ratio = window.devicePixelRatio || 1
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    if (width === size.width && height === size.height && ratio === size.ratio) return
    size = { width, height, ratio }
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
  }

  function paint() {
    frame = null
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    fit()
    ctx.setTransform(size.ratio, 0, 0, size.ratio, 0, 0)
    ctx.clearRect(0, 0, size.width, size.height)
    const now = performance.now()
    const live = board.prune(now)
    const area = rect()
    if (area) board.draw(ctx, area, now, nameFor)
    if (live) frame = requestAnimationFrame(paint)
  }

  function wake() {
    if (frame === null && typeof requestAnimationFrame === 'function') {
      frame = requestAnimationFrame(paint)
    }
  }

  let observer: ResizeObserver | null = null
  onMount(() => {
    if (!canvas) return
    observer = new ResizeObserver(wake)
    observer.observe(canvas)
  })
  onDestroy(() => {
    observer?.disconnect()
    if (frame !== null) cancelAnimationFrame(frame)
    sender.flush()
  })

  // Leaving marking mode hides our pointer for everyone.
  let wasActive = false
  $effect(() => {
    if (wasActive && !active) sender.pointer(null)
    wasActive = active
  })

  let down: { x: number; y: number; drawing: boolean } | null = null

  function at(event: PointerEvent): { px: [number, number]; point: MarkPoint | null } {
    const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const px: [number, number] = [event.clientX - bounds.left, event.clientY - bounds.top]
    const area = rect()
    return { px, point: area ? toFraction(px[0], px[1], area) : null }
  }

  function echo(kind: 'ping' | 'ink', points: MarkPoint[], stroke: number) {
    if (selfId) apply({ from: selfId, kind, stroke, points })
  }

  function onDown(event: PointerEvent) {
    if (event.button !== 0) return
    const { px, point } = at(event)
    if (!point) return
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
    down = { x: px[0], y: px[1], drawing: false }
  }

  function onMove(event: PointerEvent) {
    const { px, point } = at(event)
    if (!down) {
      sender.pointer(point)
      return
    }
    if (!down.drawing && Math.hypot(px[0] - down.x, px[1] - down.y) > DRAG_PX) {
      down.drawing = true
      const area = rect()
      const start = area ? toFraction(down.x, down.y, area) : null
      if (start) {
        sender.beginStroke(start)
        echo('ink', [start], sender.stroke)
      }
    }
    if (down.drawing && point) {
      sender.ink(point)
      echo('ink', [point], sender.stroke)
    }
  }

  function onUp(event: PointerEvent) {
    if (!down) return
    const { point } = at(event)
    if (down.drawing) {
      sender.flush()
    } else if (point) {
      sender.ping(point)
      echo('ping', [point], 0)
    }
    down = null
  }

  function onLeave() {
    if (!down) sender.pointer(null)
  }
</script>

<canvas
  bind:this={canvas}
  class="marks"
  class:marks--active={active}
  aria-hidden="true"
  onpointerdown={active ? onDown : undefined}
  onpointermove={active ? onMove : undefined}
  onpointerup={active ? onUp : undefined}
  onpointercancel={active ? onUp : undefined}
  onpointerleave={active ? onLeave : undefined}
></canvas>
